import { Box, Typography } from '@mui/material';
import config from 'config';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { onboardingHints } from '../../../constants';
import type { StoreType } from '../../../reducers';
import { getConversation, getCurrentConversationId } from '../../../reducers/aiAssistant.selectors';
import { selectUser } from '../../../reducers/user.selectors';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import useIsGeneratingReply from '../../../utils/useIsGeneratingReply';
import useIsMukibudeRoute from '../../../utils/useIsMukibudeRoute';
import useIsPodcastGenerating from '../../../utils/useIsPodcastGenerating';
import useScreenSize from '../../../utils/useScreenSize';
import { useTriggeredHint } from '../../../utils/useTriggeredHint';
import Dialog from '../../Dialog/Dialog';

const AIAssistantFeedbackDialog = () => {
	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();
	const {
		isAssistantOpen,
		wasFeedbackOpen,
		setWasFeedbackOpen,
		isFeedbackDialogOpen: isOpen,
		setIsFeedbackDialogOpen: setIsOpen,
	} = useContext(aiAssistantContext);
	const isMukibudeRoute = useIsMukibudeRoute();
	const isPodcastGenerating = useIsPodcastGenerating();
	const isGeneratingReply = useIsGeneratingReply();
	const user = useSelector(selectUser);
	const [lastConversationId, setLastConversationId] = useState<string | null>(null);
	const currentConversationId = useSelector(getCurrentConversationId);
	const lastConversation = useSelector((state: StoreType) =>
		getConversation(state, lastConversationId)
	);
	const { hasSeen: hasDismissedFeedback, record: recordFeedbackDismissed } = useTriggeredHint(
		onboardingHints.mukiBudeFeedback.notShowAgain
	);

	const handlePrimaryCTAClick = () => {
		// Open window immediately to avoid Safari popup blocker
		const url =
			user?.id && lastConversationId ? prepareFeedbackUrl(user.id, lastConversationId) : null;

		if (url) {
			window.open(url, '_blank');
		}

		setIsOpen(false);
		recordFeedbackDismissed();
	};

	const handleSecondaryCTAClick = () => {
		setIsOpen(false);
		recordFeedbackDismissed();
	};

	useEffect(() => {
		if (currentConversationId) {
			setLastConversationId(currentConversationId);
		}
	}, [currentConversationId]);

	useEffect(() => {
		if (
			!wasFeedbackOpen &&
			!isAssistantOpen &&
			isMukibudeRoute &&
			!isGeneratingReply &&
			!isPodcastGenerating &&
			lastConversation?.data?.messages &&
			lastConversation?.data?.messages.filter(({ role }) => role === 'assistant').length > 0 &&
			!hasDismissedFeedback
		) {
			setWasFeedbackOpen(true);
			setIsOpen(true);
		} else if (isAssistantOpen) {
			setIsOpen(false);
			setWasFeedbackOpen(false);
		}
	}, [
		lastConversation,
		isAssistantOpen,
		isMukibudeRoute,
		isGeneratingReply,
		isPodcastGenerating,
		setWasFeedbackOpen,
		wasFeedbackOpen,
		hasDismissedFeedback,
		setIsOpen,
	]);

	const htmlBody = (
		<Box sx={{ paddingTop: 0 }}>
			<Typography variant="body1">
				{t('You just used the Mukibude')} <br />
				{t('Help us with asnwers for 3 short questions')} <br />
				{t('Less than 1 minute')}
			</Typography>
		</Box>
	);

	const position: 'right-bottom' | 'center' = isXsScreen ? 'center' : 'right-bottom';
	const dialogProps = {
		id: onboardingHints.mukiBudeFeedback.notShowAgain,
		title: t('How do you like the new Mukibude?'),
		onClose: () => setIsOpen(false),
		primaryCta: {
			label: t('Give feedback'),
			onClick: handlePrimaryCTAClick,
		},
		secondaryCta: {
			label: t('Do not show more'),
			onClick: handleSecondaryCTAClick,
		},
		htmlBody,
		position,
		dataTestId: 'new-ai-assistant-feedback-dialog',
	};

	return <Dialog open={isOpen && isMukibudeRoute} {...dialogProps} />;
};
export default AIAssistantFeedbackDialog;

const prepareFeedbackUrl = (userId: string, conversationId: string) => {
	return config.newAiAssistantFeedbackModal.url
		.replace('{userId}', userId)
		.replace('{conversationId}', conversationId);
};
